import {
    HttpClient,
    HttpClientEnhancer,
    Jwt,
    JwtManager,
    RefreshToken,
    RefreshedTokenData,
    UploadClient,
    createAxiosWithRefresh,
    withBaseUrl,
    withJwtAuthorization,
    withMutexes,
    withStatusInterceptor,
    withUnloadCancellationPrevention,
} from '@raison/network-facade';
import {Decoder} from '@raison/prelude';
import {Action, ThunkDispatch} from '@reduxjs/toolkit';
import axios from 'axios';
import {CriticalErrorMsg, SessionExpiredMsg} from '~/entities/dialogs';
import {clearUserSession, logoutNow} from '~/entities/user';
import {SusActivityDialog} from '~/features/two-factor';
import {AppAutoUpdater} from '~/processes/app-maintenance';
import {appSessionStorage} from '~/shared/persistence';

const baseUrl = `/api`;

const AppJwtManager: JwtManager = {
    getJwt() {
        const entry = appSessionStorage.authToken.get();
        return Decoder.check(Jwt)(entry) ? entry : null;
    },

    persistJwt(jwt: Jwt) {
        return appSessionStorage.authToken.set(jwt);
    },

    getRefreshToken: () => {
        const entry = appSessionStorage.refreshToken.get();
        return Decoder.check(RefreshToken)(entry) ? entry : null;
    },

    persistRefreshToken(refreshToken: RefreshToken) {
        return appSessionStorage.refreshToken.set(refreshToken);
    },

    async fetchNewRefreshToken() {
        const refreshToken = AppJwtManager.getRefreshToken();
        if (!refreshToken) {
            return null;
        }
        const response = await self.fetch(`${baseUrl}/v1/refresh_token`, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                // eslint-disable-next-line no-restricted-globals
                'X-Referer': location.href, // do we need it?
            },
            body: JSON.stringify({refresh_token: refreshToken}),
        });
        if (!response.ok) {
            return null;
        }
        const data: unknown = await response.json();
        if (!Decoder.check(RefreshedTokenData)(data)) {
            return null;
        }
        return data;
    },
};

/**
 * @deprecated only used in api_legacy.js
 */
export const axiosWithRefresh = createAxiosWithRefresh({
    axios: axios,
    jwtManager: AppJwtManager,
});

export type CreateRnsApiClientOptions = {
    enhancers?: HttpClientEnhancer[];
};

const withLocks = withMutexes(
    CriticalErrorMsg.httpLock,
    SessionExpiredMsg.httpLock,
    AppAutoUpdater.httpLock,
    SusActivityDialog.httpLock
    //
);

export function createRsnPublicApiClient({enhancers = []}: CreateRnsApiClientOptions = {}) {
    return HttpClientEnhancer.enhance(HttpClient.base, [
        //
        ...enhancers,
        withBaseUrl(baseUrl),
    ]);
}

export function createRsnApiClient({enhancers = []}: CreateRnsApiClientOptions = {}) {
    return HttpClientEnhancer.enhance(HttpClient.base, [
        ...enhancers,
        withLocks,
        withUnloadCancellationPrevention,
        withJwtAuthorization(AppJwtManager),
        withBaseUrl(baseUrl),
    ]);
}

export function createRsnUploadClient({enhancers = []}: CreateRnsApiClientOptions = {}) {
    return HttpClientEnhancer.enhance(UploadClient.base, [
        ...enhancers,
        withLocks,
        withJwtAuthorization(AppJwtManager),
        withBaseUrl(baseUrl),
    ]);
}

export function with401Interceptor(
    getStore: () => {
        dispatch: ThunkDispatch<any, any, Action>;
    }
): HttpClientEnhancer {
    return withStatusInterceptor(401, (_, {abort}) => {
        const store = getStore();
        store.dispatch(
            SessionExpiredMsg.open({
                onConfirm: () => store.dispatch(logoutNow()),
            })
        );
        clearUserSession();
        return abort();
    });
}
